import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import agent from "../../../agent";

/**
 * Настраиваемые параметры приложения — nsi.app_settings.
 *
 * Список параметров закрыт: каждый заводится миграцией вместе с кодом, который
 * его читает. Здесь меняется только значение, поэтому ни создания, ни удаления
 * на экране нет.
 *
 * Правка уходит одним запросом на всю таблицу: сервер применяет её в одной
 * транзакции, и неверное значение не оставит половину настроек записанной.
 */
const AppSettings = () => {
    const toast = React.useRef(null);

    const [settings, setSettings] = React.useState([]);
    const [drafts, setDrafts] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const showError = (detail) =>
        toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 6000 });
    const showSuccess = (detail) =>
        toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });

    const load = React.useCallback(() => {
        setLoading(true);
        agent.AppSettings.list()
            .then((res) => {
                setSettings(Array.isArray(res) ? res : []);
                setDrafts({});
            })
            .catch((err) => showError(typeof err === "string" ? err : agent.handleError(err)))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    /** Значение в поле: правка оператора, если она была, иначе сохранённое. */
    const valueOf = (row) => (row.code in drafts ? drafts[row.code] : row.value);

    const setValue = (row, value) => {
        setDrafts((prev) => {
            const next = { ...prev };
            // Возврат к исходному значению — это не правка: иначе кнопка
            // «Сохранить» останется активной после отмены руками.
            if (String(value) === String(row.value ?? "")) {
                delete next[row.code];
            } else {
                next[row.code] = value == null ? "" : String(value);
            }
            return next;
        });
    };

    const changed = Object.keys(drafts);

    const onSave = () => {
        if (!changed.length) return;
        setSaving(true);
        agent.AppSettings.update(changed.map((code) => ({ code, value: drafts[code] })))
            .then((res) => {
                setSettings(Array.isArray(res) ? res : []);
                setDrafts({});
                showSuccess(changed.length === 1 ? "Параметр сохранён" : "Параметры сохранены");
            })
            .catch((err) => showError(typeof err === "string" ? err : agent.handleError(err)))
            .finally(() => setSaving(false));
    };

    const valueBody = (row) => {
        const value = valueOf(row);
        const edited = row.code in drafts;
        const style = edited ? { borderColor: "#f59e0b" } : undefined;

        if (row.valueType === "ENUM" && Array.isArray(row.options)) {
            return (
                <Dropdown
                    value={value == null ? null : String(value)}
                    options={row.options}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => setValue(row, e.value)}
                    style={{ width: "100%", ...style }}
                />
            );
        }

        if (row.valueType === "BOOLEAN") {
            return (
                <InputSwitch
                    checked={String(value) === "true"}
                    onChange={(e) => setValue(row, e.value ? "true" : "false")}
                />
            );
        }

        if (row.valueType === "INTEGER" || row.valueType === "DECIMAL") {
            return (
                <InputNumber
                    value={value === "" || value == null ? null : Number(value)}
                    onValueChange={(e) => setValue(row, e.value)}
                    mode="decimal"
                    useGrouping
                    maxFractionDigits={row.valueType === "DECIMAL" ? 3 : 0}
                    inputStyle={style}
                    style={{ width: "100%" }}
                />
            );
        }

        return (
            <InputText
                value={value == null ? "" : value}
                onChange={(e) => setValue(row, e.target.value)}
                style={{ width: "100%", ...style }}
            />
        );
    };

    const nameBody = (row) => (
        <div>
            <div>{row.nameRu}</div>
            <small style={{ color: "#6b7280" }}>{row.code}</small>
        </div>
    );

    const modifiedBody = (row) =>
        row.lastModifiedDate ? String(row.lastModifiedDate).replace("T", " ").slice(0, 16) : "—";

    return (
        <div>
            <Toast ref={toast} />

            <div className="p-d-flex p-jc-between p-ai-center">
                <h1>Параметры приложения</h1>
                <Button
                    label="Сохранить"
                    icon="pi pi-check"
                    loading={saving}
                    disabled={!changed.length}
                    onClick={onSave}
                />
            </div>

            <Message
                severity="info"
                style={{ marginBottom: 10 }}
                text="Значения действуют сразу, без выката. Лимит постоплаты и срок оплаты АВР применяются ко всем юрлицам — в карточке клиента они больше не задаются."
            />

            <DataTable
                value={settings}
                loading={loading}
                dataKey="code"
                rowGroupMode="subheader"
                groupRowsBy="groupRu"
                sortField="groupRu"
                sortOrder={1}
                rowGroupHeaderTemplate={(row) => <span style={{ fontWeight: 700 }}>{row.groupRu || "Прочее"}</span>}
                emptyMessage="Параметры не заведены"
            >
                <Column header="Параметр" body={nameBody} style={{ width: "26%" }} />
                <Column field="description" header="Описание" style={{ width: "40%" }} />
                <Column header="Значение" body={valueBody} style={{ width: "22%" }} />
                <Column header="Изменён" body={modifiedBody} style={{ width: "12%" }} />
            </DataTable>
        </div>
    );
};

export default AppSettings;
